import React, { useEffect, useRef, useState } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthModal from './AuthModal';
import logo from '../assets/poupix-logo.png';
import './Header.css';

const Header = ({ searchTerm, onSearch, onCartClick, onLogoClick, isCartOpen, onHeaderInteract }) => {
  const [inputValue, setInputValue] = useState(searchTerm || '');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);
  const inputRef = useRef(null);
  const { cartItems, lastAdded } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const { language, setLanguage, supportedLanguages, t } = useI18n();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const showSearchBar = location.pathname !== '/';

  // Hide header on scroll-down, reveal on scroll-up.
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 60) { setHidden(false); lastScrollY.current = y; return; }
      if (y > lastScrollY.current + 6) setHidden(true);
      else if (y < lastScrollY.current - 4) setHidden(false);
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setInputValue(searchTerm || '');
  }, [searchTerm]);

  const runSearch = (value) => {
    if (location.pathname !== '/products') {
      navigate('/products');
    }
    onSearch(value);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    runSearch(value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    runSearch(inputValue);
  };

  const handleClearSearch = () => {
    setInputValue('');
    runSearch('');
    inputRef.current?.focus();
  };

  useEffect(() => {
    const onClickOutside = (event) => {
      if (!event.target.closest('.lang-menu')) {
        setIsLangMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <>
      <header
        className={`header${hidden ? ' header--hidden' : ''}`}
        onMouseDownCapture={() => {
          if (isCartOpen && onHeaderInteract) {
            onHeaderInteract();
          }
        }}
      >
        <div className="header-container">
          <div className="header-top-row">
            <div className="logo">
              <Link to="/" className="logo-link" onClick={() => onLogoClick && onLogoClick()}>
                <img src={logo} alt="Poupix" className="logo-image" />
              </Link>
            </div>

            <div className="mobile-header-controls">
              <div className="lang-menu mobile-lang-menu">
                <button
                  type="button"
                  className="lang-btn mobile-lang-btn"
                  onClick={() => setIsLangMenuOpen((prev) => !prev)}
                  aria-label={t('language.menuLabel')}
                  aria-expanded={isLangMenuOpen}
                >
                  <span aria-hidden="true">🌐</span>
                </button>

                {isLangMenuOpen && (
                  <div className="lang-dropdown" role="menu" aria-label={t('language.menuLabel')}>
                    {supportedLanguages.map((lang) => (
                      <button
                        key={lang}
                        type="button"
                        className={`lang-option${language === lang ? ' active' : ''}`}
                        onClick={() => {
                          setLanguage(lang);
                          setIsLangMenuOpen(false);
                        }}
                      >
                        {t(`language.${lang}`)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                className="mobile-auth-btn"
                onClick={() => {
                  if (isAuthenticated) {
                    logout();
                  } else {
                    setShowAuthModal(true);
                  }
                }}
              >
                {isAuthenticated ? t('header.logout') : t('header.login')}
              </button>
            </div>
          </div>

          {showSearchBar && (
            <form className="search-bar" onSubmit={handleSearchSubmit}>
              <div className="search-input-wrap">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder={t('header.searchPlaceholder')}
                  value={inputValue}
                  onChange={handleSearchChange}
                  className="search-input"
                />
                {inputValue && (
                  <button
                    type="button"
                    className="search-clear-btn"
                    onClick={handleClearSearch}
                    aria-label={t('header.clearSearch')}
                  >
                    ✕
                  </button>
                )}
              </div>
              <button type="submit" className="search-button" aria-label={t('common.search')}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M10.5 3a7.5 7.5 0 0 1 5.91 12.11l4.24 4.23-1.42 1.42-4.23-4.24A7.5 7.5 0 1 1 10.5 3Zm0 2a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Z" />
                </svg>
              </button>
            </form>
          )}

          <div className="header-actions">
            <div className="lang-menu desktop-lang-menu">
              <button
                type="button"
                className="lang-btn"
                onClick={() => setIsLangMenuOpen((prev) => !prev)}
                aria-label={t('language.menuLabel')}
                aria-expanded={isLangMenuOpen}
              >
                <span aria-hidden="true">🌐</span>
              </button>

              {isLangMenuOpen && (
                <div className="lang-dropdown" role="menu" aria-label={t('language.menuLabel')}>
                  {supportedLanguages.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      className={`lang-option${language === lang ? ' active' : ''}`}
                      onClick={() => {
                        setLanguage(lang);
                        setIsLangMenuOpen(false);
                      }}
                    >
                      {t(`language.${lang}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {isAuthenticated ? (
              <>
                <span className="auth-user">{t('header.hiUser', { name: user?.display_name || user?.username || user?.email })}</span>
                <button className="auth-btn secondary" onClick={logout}>{t('header.logout')}</button>
              </>
            ) : (
              <button
                className="auth-btn"
                onClick={() => {
                  setShowAuthModal(true);
                }}
              >
                {t('header.login')}
              </button>
            )}

            <button
              className={`cart-button${lastAdded ? ' cart-button--active' : ''}`}
              onClick={onCartClick}
              data-cart-anchor="true"
            >
              <svg className="cart-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 4H5L7 14H18L21 7H8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="9" cy="19" r="1.4" fill="currentColor" />
                <circle cx="18" cy="19" r="1.4" fill="currentColor" />
              </svg>
              <span className="cart-count">{cartItems.length}</span>
            </button>
          </div>
        </div>
      </header>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
};

export default Header;
